import { useEffect, useState, type JSX } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { api } from '../../api.ts';

/**
 * The teacher's side of the building.
 *
 * A separate area rather than edit affordances sprinkled through the student pages: the two
 * audiences want opposite things from the same screen. A learner wants one next step; a teacher
 * wants the whole structure at once, and wants to be sure they are not quietly rewriting
 * something a class is reading right now.
 */

/** Which tabs carry a count, and where that count comes from. */
type Waiting = 'work' | 'messages';

const TABS: { to: string; label: string; end: boolean; waiting?: Waiting }[] = [
  { to: '/admin', label: 'Курсы', end: true },
  { to: '/admin/queue', label: 'Проверка работ', end: false, waiting: 'work' },
  { to: '/admin/questions', label: 'Банк вопросов', end: false },
  { to: '/admin/students', label: 'Ученики', end: false },
  { to: '/admin/conversations', label: 'Сообщения', end: false, waiting: 'messages' },
  { to: '/admin/certificates', label: 'Сертификаты', end: false },
];

/** Slow on purpose: work waiting half a minute is not news, a poll every second is waste. */
const POLL_MS = 30_000;

/**
 * What is waiting for the teacher, on the tabs where it waits.
 *
 * Polled rather than pushed: a school of this size does not need a socket held open all day to
 * learn that one piece of homework arrived. It re-reads on every navigation too, which is what
 * actually clears a badge — opening the queue marks nothing, but leaving it and coming back asks
 * again, and by then the work has been graded.
 */
function useWaiting(): { work: number; messages: number } {
  const [counts, setCounts] = useState({ work: 0, messages: 0 });
  const location = useLocation();

  useEffect(() => {
    let alive = true;

    const read = (): void => {
      // A failed poll leaves the last known numbers alone. Flashing to nought on a dropped
      // connection would say "nothing is waiting" — the one thing a badge must never say wrongly.
      void api.authoring
        .queue()
        .then((rows) => alive && setCounts((c) => ({ ...c, work: rows.length })))
        .catch(() => undefined);

      void api.authoring
        .unread()
        .then((r) => alive && setCounts((c) => ({ ...c, messages: r.unread })))
        .catch(() => undefined);
    };

    read();
    const timer = setInterval(read, POLL_MS);

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [location.pathname]);

  return counts;
}

export function AdminLayout(): JSX.Element {
  const waiting = useWaiting();

  return (
    <>
      <nav className="adminnav" aria-label="Разделы админки">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => `adminnav__tab${isActive ? ' adminnav__tab--on' : ''}`}
          >
            {tab.label}
            {/*
             * Silent at nought. A badge showing a zero is noise that teaches people to stop
             * looking at badges, and then the one that matters goes unread too.
             */}
            {tab.waiting && waiting[tab.waiting] > 0 ? (
              <span className="adminnav__count">{waiting[tab.waiting]}</span>
            ) : null}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </>
  );
}
